/* eslint-disable prettier/prettier */
/**
 * People Picker Component
 * Searchable dropdown for selecting message recipients
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Input,
  Tag,
  TagGroup,
  Avatar,
  Text,
  Spinner,
  mergeClasses,
} from '@fluentui/react-components';
import { DismissRegular, SearchRegular, ChevronDownRegular } from '@fluentui/react-icons';
import { ApiClient } from '../../../../configurations/index.config';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: '32px',
    paddingLeft: '12px',
    paddingRight: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderRightWidth: '1px',
    borderTopStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderRightStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase300,
    fontFamily: tokens.fontFamilyBase,
    color: tokens.colorNeutralForeground1,
    transitionProperty: 'border-color',
    transitionDuration: '0.1s',
    boxSizing: 'border-box',
    ':hover': {
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
    },
  },
  triggerOpen: {
    borderTopColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
  },
  triggerText: {
    flex: 1,
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  placeholder: {
    color: tokens.colorNeutralForeground4,
  },
  chevron: {
    marginLeft: '8px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    transitionProperty: 'transform',
    transitionDuration: '0.1s',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  popoverSurface: {
    paddingTop: '0',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
    minWidth: '300px',
    maxHeight: '400px',
    overflow: 'hidden',
    boxShadow: tokens.shadow16,
  },
  searchContainer: {
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  searchInput: {
    width: '100%',
  },
  optionsList: {
    maxHeight: '300px',
    overflowY: 'auto',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '12px',
    paddingRight: '12px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  optionContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
  },
  optionText: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  tagGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  noResults: {
    paddingTop: '20px',
    paddingBottom: '20px',
    paddingLeft: '12px',
    paddingRight: '12px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

interface Recipient {
  mailbox_id: number;
  user_id: number;
  friendly_alias: string;
  full_name: string;
  department: string;
  role: string;
}

interface PeoplePickerProps {
  label: string;
  selectedRecipients: Recipient[];
  onRecipientsChange: (recipients: Recipient[]) => void;
  placeholder?: string;
  required?: boolean;
}

export const PeoplePicker: React.FC<PeoplePickerProps> = ({
  label,
  selectedRecipients,
  onRecipientsChange,
  placeholder = 'Search by name or alias...',
  required = false,
}) => {
  const styles = useStyles();
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
      setSearchResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchValue.length >= 2) {
      searchRecipients(searchValue);
    } else {
      setSearchResults([]);
    }
  }, [searchValue]);

  const searchRecipients = async (query: string) => {
    try {
      setLoading(true);
      const response = await ApiClient.get('/memo/directory', {
        params: {
          search: query,
          limit: 20,
        },
      });

      if (response.data.status === 'success') {
        const results = response.data.data || [];
        // Filter out already selected recipients
        const filtered = results.filter(
          (r: Recipient) =>
            !selectedRecipients.find(selected => selected.mailbox_id === r.mailbox_id)
        );
        setSearchResults(filtered);
      }
    } catch (error) {
      console.error('Error searching recipients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRecipient = (recipient: Recipient) => {
    onRecipientsChange([...selectedRecipients, recipient]);
    setSearchValue('');
    setSearchResults([]);
  };

  const handleRemoveRecipient = (mailboxId: number) => {
    onRecipientsChange(selectedRecipients.filter(r => r.mailbox_id !== mailboxId));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div className={styles.container}>
      <label className={styles.label}>
        {label}
        {required && <span style={{ color: '#d13438' }}> *</span>}
      </label>

      <Popover
        open={isOpen}
        onOpenChange={(_, data) => setIsOpen(data.open)}
        positioning="below-start"
        trapFocus
      >
        <PopoverTrigger disableButtonEnhancement>
          <button
            ref={triggerRef}
            type="button"
            className={mergeClasses(
              styles.trigger,
              isOpen && styles.triggerOpen
            )}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span className={mergeClasses(styles.triggerText, styles.placeholder)}>
              {placeholder}
            </span>
            <ChevronDownRegular
              className={mergeClasses(styles.chevron, isOpen && styles.chevronOpen)}
            />
          </button>
        </PopoverTrigger>

        <PopoverSurface className={styles.popoverSurface} onKeyDown={handleKeyDown}>
          <div className={styles.searchContainer}>
            <Input
              ref={searchInputRef}
              className={styles.searchInput}
              placeholder="Search by name or alias..."
              value={searchValue}
              onChange={(_, data) => setSearchValue(data.value)}
              contentBefore={<SearchRegular />}
              size="small"
            />
          </div>

          <div className={styles.optionsList} role="listbox">
            {loading && (
              <div className={styles.loadingContainer}>
                <Spinner size="small" label="Searching..." />
              </div>
            )}
            {!loading && searchValue.length < 2 && (
              <div className={styles.noResults}>
                Type at least 2 characters to search
              </div>
            )}
            {!loading && searchValue.length >= 2 && searchResults.length === 0 && (
              <div className={styles.noResults}>
                No results found
              </div>
            )}
            {!loading &&
              searchResults.map(recipient => (
                <button
                  key={recipient.mailbox_id}
                  type="button"
                  className={styles.option}
                  onClick={() => handleSelectRecipient(recipient)}
                >
                  <div className={styles.optionContent}>
                    <Avatar name={recipient.full_name} size={32} />
                    <div className={styles.optionText}>
                      <Text weight="semibold">{recipient.full_name}</Text>
                      <Text size={200} style={{ color: '#605e5c' }}>
                        {recipient.friendly_alias} • {recipient.department}
                      </Text>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </PopoverSurface>
      </Popover>

      {selectedRecipients.length > 0 && (
        <TagGroup className={styles.tagGroup} onDismiss={(_, data) => handleRemoveRecipient(parseInt(data.value))}>
          {selectedRecipients.map(recipient => (
            <Tag
              key={recipient.mailbox_id}
              dismissible
              dismissIcon={<DismissRegular />}
              value={recipient.mailbox_id.toString()}
            >
              <Avatar name={recipient.full_name} size={20} style={{ marginRight: '4px' }} />
              {recipient.full_name} ({recipient.friendly_alias})
            </Tag>
          ))}
        </TagGroup>
      )}
    </div>
  );
};

export default PeoplePicker;
